
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { NetworkDevice, Link, NodeType, PingResult } from './types';
import DeviceNode from './components/DeviceNode';
import { simulatePing, isValidIP, findPath } from './utils/networkLogic';

const App: React.FC = () => {
  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linkingFrom, setLinkingFrom] = useState<string | null>(null);
  const [pingIp, setPingIp] = useState('');
  const [pingLog, setPingLog] = useState<string[]>([]);
  const [activePath, setActivePath] = useState<string[]>([]);
  const [showCertificate, setShowCertificate] = useState(false);
  const [userName, setUserName] = useState('');
  const [userClass, setUserClass] = useState('');
  const [zoom, setZoom] = useState(1);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  
  const LOGO_URL = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRUAewpaCEiTBuf9_nlRFk0cA01o876EqM6lw&s";

  const deviceCounters = useRef<Record<NodeType, number>>({
    PC: 0,
    SWITCH: 0,
    ROUTER: 0,
    ISP: 0
  });

  const [missionProgress, setMissionProgress] = useState({
    misi1: false, // Tarik Kabel ISP (ISP -> Router)
    misi2: false, // Instalasi LAN (Router -> Switch -> PC)
    misi3: false  // Online (Ping 100.100.100.1 Sukses)
  });

  const panStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const pcs = devices.filter(d => d.type === 'PC');
    const switches = devices.filter(d => d.type === 'SWITCH');
    const router = devices.find(d => d.type === 'ROUTER');
    const isp = devices.find(d => d.id === 'isp-master');

    // Misi 1: Tarik Kabel ISP (Cek apakah Router sudah terhubung ke ISP)
    if (router && isp) {
      const isConnectedToIsp = links.some(l => 
        (l.from === router.id && l.to === isp.id) || (l.to === router.id && l.from === isp.id)
      );
      if (isConnectedToIsp && !missionProgress.misi1) {
        setMissionProgress(prev => ({ ...prev, misi1: true }));
        setPingLog(prev => [`[Achievement] Misi 1: Kabel dari Tiang ISP sudah masuk ke Modem!`, ...prev.slice(0, 9)]);
      }
    }

    // Misi 2: Instalasi LAN (Cek jalur internal: Router -> Switch -> PC)
    if (missionProgress.misi1 && router && switches.length > 0 && pcs.length > 0) {
      const routerToSwitch = links.some(l => {
        const fromT = devices.find(d => d.id === l.from)?.type;
        const toT = devices.find(d => d.id === l.to)?.type;
        return (fromT === 'ROUTER' && toT === 'SWITCH') || (fromT === 'SWITCH' && toT === 'ROUTER');
      });
      
      const switchToPc = links.some(l => {
        const fromT = devices.find(d => d.id === l.from)?.type;
        const toT = devices.find(d => d.id === l.to)?.type;
        return (fromT === 'SWITCH' && toT === 'PC') || (fromT === 'PC' && toT === 'SWITCH');
      });

      if (routerToSwitch && switchToPc && !missionProgress.misi2) {
        setMissionProgress(prev => ({ ...prev, misi2: true }));
        setPingLog(prev => [`[Achievement] Misi 2: Jaringan kabel di dalam rumah selesai dipasang!`, ...prev.slice(0, 9)]);
      }
    }
  }, [links, devices, missionProgress]);

  useEffect(() => {
    if (devices.length === 0) {
      setDevices([{
        id: 'isp-master',
        type: 'ISP',
        name: 'TIANG-ISP-PROV',
        x: 80,
        y: 80,
        ip: '100.100.100.1',
        subnet: '255.255.255.0'
      }]);
    }
  }, []);

  const addDevice = (type: NodeType) => {
    if (type === 'ISP') return;
    deviceCounters.current[type] += 1;
    const currentNumber = deviceCounters.current[type];
    const id = Math.random().toString(36).substr(2, 9);
    
    const newDevice: NetworkDevice = {
      id,
      type,
      name: type === 'ROUTER' ? 'Modem-Rumah' : (type === 'SWITCH' ? 'Hub-Internal' : `PC-Anak-${currentNumber}`),
      x: (window.innerWidth / 2 - canvasOffset.x - 40) / zoom,
      y: (window.innerHeight / 2 - canvasOffset.y - 40) / zoom,
      ip: '',
      ipWan: '',
      subnet: '255.255.255.0',
      gateway: '',
      firewallDisabled: false
    };
    
    setDevices(prev => [...prev, newDevice]);
    setSelectedId(id);
    setPingLog(prev => [`[System] Memasang ${newDevice.name}...`, ...prev.slice(0, 9)]);
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsPanning(true);
      panStart.current = { x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y };
      setSelectedId(null);
    }
  };

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleDeviceMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (linkingFrom) {
      if (linkingFrom !== id) {
        const fromDev = devices.find(d => d.id === linkingFrom);
        const toDev = devices.find(d => d.id === id);
        
        if ((fromDev?.type === 'ISP' && toDev?.type !== 'ROUTER') || (toDev?.type === 'ISP' && fromDev?.type !== 'ROUTER')) {
           setPingLog(prev => [`[Warning] Kabel luar (ISP) harus dicolok ke Modem dulu!`, ...prev.slice(0, 9)]);
           setLinkingFrom(null);
           return;
        }

        const alreadyLinked = links.some(l => (l.from === linkingFrom && l.to === id) || (l.from === id && l.to === linkingFrom));
        if (!alreadyLinked) {
          setLinks(prev => [...prev, { id: `link-${Date.now()}`, from: linkingFrom, to: id }]);
          setPingLog(prev => [`[Link] ${fromDev?.name} tersambung ke ${toDev?.name}`, ...prev.slice(0, 9)]);
        }
      }
      setLinkingFrom(null);
      return;
    }
    const device = devices.find(d => d.id === id);
    if (device) {
      setDraggingId(id);
      setSelectedId(id);
      dragOffset.current = { x: e.clientX - (device.x * zoom + canvasOffset.x), y: e.clientY - (device.y * zoom + canvasOffset.y) };
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingId) {
      setDevices(prev => prev.map(d => 
        d.id === draggingId ? { 
          ...d, 
          x: (e.clientX - dragOffset.current.x - canvasOffset.x) / zoom, 
          y: (e.clientY - dragOffset.current.y - canvasOffset.y) / zoom 
        } : d
      ));
    } else if (isPanning) {
      setCanvasOffset({
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y
      });
    }
  };

  const handleMouseUp = () => {
    setDraggingId(null);
    setIsPanning(false);
  };

  const handlePing = () => {
    if (!selectedId || !pingIp) return;
    const source = devices.find(d => d.id === selectedId);
    if (!source) return;

    if (pingIp === '100.100.100.1') {
      const path = findPath(selectedId, 'isp-master', devices, links);
      if (path) {
        const routerInPath = path.map(id => devices.find(d => d.id === id)).find(d => d?.type === 'ROUTER');
        if (routerInPath && source.gateway === routerInPath.ip && routerInPath.ipWan === '100.100.100.2') {
          setPingLog(prev => [`[CMD] ping 100.100.100.1`, `[SUKSES] Koneksi ke Pusat Aktif! Internet Menyala.`, ...prev.slice(0, 8)]);
          setMissionProgress(prev => ({ ...prev, misi3: true }));
          setActivePath(path);
          setTimeout(() => { setActivePath([]); setShowCertificate(true); }, 2000);
          return;
        }
      }
    }

    const result = simulatePing(selectedId, pingIp, devices, links);
    setPingLog(prev => [`[CMD] ping ${pingIp}`, `[Log] ${result.message}`, ...prev.slice(0, 8)]);
    if (result.success) {
      setActivePath(result.path);
      setTimeout(() => setActivePath([]), 2000);
    }
  };

  const updateDeviceConfig = (id: string, updates: Partial<NetworkDevice>) => {
    setDevices(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const handleZoom = (direction: 'in' | 'out' | 'reset') => {
    if (direction === 'in') setZoom(prev => Math.min(prev + 0.1, 2));
    else if (direction === 'out') setZoom(prev => Math.max(prev - 0.1, 0.5));
    else {
      setZoom(1);
      setCanvasOffset({ x: 0, y: 0 });
    }
  };

  const selectedDevice = devices.find(d => d.id === selectedId);

  return (
    <div className="flex flex-col h-screen overflow-hidden text-slate-900 bg-slate-50 font-sans">
      {showCertificate && (
        <div className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white max-w-4xl w-full rounded-[40px] shadow-2xl overflow-hidden flex flex-col md:flex-row h-auto md:h-[600px] border-8 border-white animate-in zoom-in-95 duration-300">
            <div className="w-full md:w-80 bg-slate-50 p-8 flex flex-col justify-center border-r border-slate-100">
              <h2 className="text-2xl font-black text-slate-800 leading-tight mb-2">Hebat!<br/><span className="text-blue-600">Teknisi Selesai.</span></h2>
              <div className="space-y-4 mb-8">
                <input className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 outline-none focus:border-blue-500 font-bold" placeholder="Nama Anda" value={userName} onChange={(e) => setUserName(e.target.value)} />
                <input className="w-full px-4 py-3 rounded-2xl border-2 border-slate-200 outline-none focus:border-blue-500 font-bold" placeholder="Kelas" value={userClass} onChange={(e) => setUserClass(e.target.value)} />
              </div>
              <button onClick={() => window.print()} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg active:scale-95 transition-transform">Cetak Sertifikat</button>
              <button onClick={() => setShowCertificate(false)} className="w-full py-4 mt-2 bg-white text-slate-400 rounded-2xl font-black text-xs uppercase tracking-widest border border-slate-100">Cek Lagi</button>
            </div>
            <div className="flex-1 bg-white p-12 relative flex flex-col items-center justify-center text-center">
               <img src={LOGO_URL} alt="Logo" className="w-20 h-20 mb-6 rounded-2xl shadow-lg" />
               <h4 className="text-3xl font-black text-slate-800 mb-8 uppercase tracking-tighter">Certified Home Network Specialist</h4>
               <p className="text-3xl font-black text-slate-800 border-b-4 border-blue-500 inline-block px-4 pb-1">{userName || "..................."}</p>
               <p className="text-sm font-bold text-slate-500 mt-4 italic">Berhasil melakukan instalasi Internet Provider ke Rumah</p>
               <p className="text-xs font-bold text-slate-400 mt-1 uppercase tracking-widest">Kelas: {userClass || "---"}</p>
            </div>
          </div>
        </div>
      )}

      <header className="bg-white border-b px-6 py-2 flex items-center justify-between shadow-sm z-30">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain rounded shadow-sm" />
          <div>
            <h1 className="text-sm font-black uppercase tracking-tight text-slate-800 leading-none">NetSim Junior</h1>
            <p className="text-[8px] text-slate-400 font-bold uppercase tracking-widest leading-none mt-1">Skenario: Instalasi Provider Internet</p>
          </div>
        </div>
        <div className="flex gap-2">
          {['PC', 'SWITCH', 'ROUTER'].map((type) => (
            <button key={type} onClick={() => addDevice(type as NodeType)} className="px-3 py-1.5 bg-white border-2 border-slate-100 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-all text-[10px] font-black uppercase tracking-widest shadow-sm active:scale-95 text-slate-600">
              + {type === 'ROUTER' ? 'MODEM' : type}
            </button>
          ))}
        </div>
      </header>

      <div className="flex flex-1 relative overflow-hidden">
        <div className="flex flex-col flex-1 relative overflow-hidden">
          <main 
            className={`flex-1 relative canvas-grid bg-white overflow-hidden cursor-${isPanning ? 'grabbing' : 'grab'}`} 
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleMouseMove} 
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div 
              className="absolute inset-0 transition-transform duration-200 origin-top-left" 
              style={{ transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})` }}
            >
              <svg className="absolute inset-0 min-w-full min-h-full pointer-events-none overflow-visible" style={{ width: '5000px', height: '5000px' }}>
                {links.map(link => {
                  const from = devices.find(d => d.id === link.from);
                  const to = devices.find(d => d.id === link.to);
                  if (!from || !to) return null;
                  const isActive = activePath.includes(link.from) && activePath.includes(link.to);
                  const isIspLink = from.type === 'ISP' || to.type === 'ISP';
                  return (
                    <g key={link.id} className="cursor-pointer pointer-events-auto group" onClick={(e) => { e.stopPropagation(); setLinks(prev => prev.filter(l => l.id !== link.id)); }}>
                      <line x1={from.x + 40} y1={from.y + 40} x2={to.x + 40} y2={to.y + 40} stroke={isIspLink ? '#3b82f6' : (isActive ? '#10b981' : '#cbd5e1')} strokeWidth={isActive ? 8 : 4} strokeLinecap="round" className={isActive ? 'animate-pulse' : ''} strokeDasharray={isIspLink ? "none" : "10,10"} />
                    </g>
                  );
                })}
              </svg>
              {devices.map(device => (
                <div key={device.id} onMouseDown={(e) => handleDeviceMouseDown(e, device.id)} className="absolute z-10" style={{ left: device.x, top: device.y }}>
                  <DeviceNode device={device} isSelected={selectedId === device.id} onClick={() => {}} />
                </div>
              ))}
            </div>

            <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-50">
              <div className="bg-white/90 backdrop-blur shadow-lg border border-slate-200 rounded-xl p-0.5 flex flex-col items-center">
                <button onClick={() => handleZoom('in')} className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-slate-600 rounded-lg transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" /></svg></button>
                <button onClick={() => handleZoom('reset')} className="px-1 py-0.5 text-[9px] font-black text-slate-400 hover:text-blue-500 uppercase tracking-tighter">{Math.round(zoom * 100)}%</button>
                <button onClick={() => handleZoom('out')} className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-slate-600 rounded-lg transition-all"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" /></svg></button>
              </div>
              <div className="text-center">
                <p className="text-[7px] font-black text-slate-300 uppercase tracking-widest">Tarik Kanvas untuk Geser</p>
              </div>
            </div>
          </main>

          <section className="h-32 bg-white border-t z-20 flex flex-col shadow-inner">
            <div className="px-4 py-1.5 border-b bg-slate-50/80 flex justify-between items-center">
              <h2 className="text-[9px] font-black uppercase text-slate-500 tracking-widest">Tugas Pemasangan Provider</h2>
              <div className="text-[8px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-100 uppercase tracking-tight">IP Gateway ISP: 100.100.100.1</div>
            </div>
            <div className="flex-1 overflow-x-auto p-3 flex gap-3 items-center scrollbar-hide">
                {[
                  { id: 'misi1', title: '1. Tarik Kabel ISP', desc: 'Hubungkan Tiang ISP (Luar) ke Modem Rumah.', done: missionProgress.misi1 },
                  { id: 'misi2', title: '2. Jaringan Rumah', desc: 'Modem -> Switch -> PC. Pasang semua kabel LAN.', done: missionProgress.misi2 },
                  { id: 'misi3', title: '3. Onlinekan!', desc: 'Isi IP & Gateway. Ping 100.100.100.1 dari PC.', done: missionProgress.misi3 }
                ].map((step, idx) => (
                  <div key={step.id} className={`w-52 p-3 rounded-xl border shrink-0 transition-all ${step.done ? 'border-green-200 bg-green-50' : 'border-slate-100 bg-white shadow-sm'}`}>
                    <div className="flex gap-3 items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${step.done ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400'}`}>{step.done ? '✓' : idx + 1}</div>
                      <div>
                        <p className={`text-[10px] font-black uppercase ${step.done ? 'text-green-700' : 'text-slate-700'}`}>{step.title}</p>
                        <p className="text-[9px] text-slate-500 leading-tight font-medium">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </section>
        </div>

        <aside className="w-72 bg-white border-l shadow-2xl z-20 flex flex-col">
          <div className="p-4 flex-1 overflow-y-auto scrollbar-hide">
            {selectedDevice ? (
              <div className="space-y-5 animate-in slide-in-from-right-4 duration-200">
                <div className="flex justify-between items-center border-b pb-3">
                  <div>
                    <h2 className="text-[9px] font-black uppercase text-slate-400 tracking-widest">{selectedDevice.type}</h2>
                    <p className="text-xs font-bold text-slate-800">{selectedDevice.name}</p>
                  </div>
                  {selectedDevice.type !== 'ISP' && (
                    <button onClick={() => { setDevices(prev => prev.filter(d => d.id !== selectedId)); setLinks(prev => prev.filter(l => l.from !== selectedId && l.to !== selectedId)); setSelectedId(null); }} className="text-red-500 text-[9px] font-black hover:bg-red-50 px-2 py-1 rounded-lg border border-transparent hover:border-red-100 transition-all">HAPUS</button>
                  )}
                </div>

                <div className="space-y-4">
                  <section className="bg-slate-50 p-3 rounded-xl border border-slate-100 shadow-inner">
                    <label className="text-[9px] font-black text-slate-400 block uppercase mb-2">Konfigurasi Alat</label>
                    <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[11px] font-bold mb-3 outline-none focus:border-blue-500 shadow-sm" value={selectedDevice.name} onChange={(e) => updateDeviceConfig(selectedId!, { name: e.target.value })} placeholder="Nama" />
                    
                    {selectedDevice.type === 'ROUTER' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[8px] font-bold text-blue-500 uppercase px-1">IP WAN (Sisi ISP)</label>
                          <input className="w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-[11px] font-mono text-blue-700 font-bold" value={selectedDevice.ipWan || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ipWan: e.target.value })} placeholder="Isi: 100.100.100.2" />
                        </div>
                        <div>
                          <label className="text-[8px] font-bold text-slate-400 uppercase px-1">IP LAN (Gateway Lokal)</label>
                          <input className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-mono font-bold" value={selectedDevice.ip || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ip: e.target.value })} placeholder="Ex: 192.168.1.1" />
                        </div>
                      </div>
                    )}

                    {selectedDevice.type === 'PC' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[8px] font-bold text-slate-400 uppercase px-1">IP Address PC</label>
                          <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[11px] font-mono font-bold" value={selectedDevice.ip || ''} onChange={(e) => updateDeviceConfig(selectedId!, { ip: e.target.value })} placeholder="Ex: 192.168.1.10" />
                        </div>
                        <div>
                          <label className="text-[8px] font-bold text-slate-400 uppercase px-1">Default Gateway</label>
                          <input className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[11px] font-mono font-bold" value={selectedDevice.gateway || ''} onChange={(e) => updateDeviceConfig(selectedId!, { gateway: e.target.value })} placeholder="IP LAN Modem" />
                        </div>
                      </div>
                    )}
                  </section>

                  {selectedDevice.type === 'PC' && (
                    <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-blue-50 shadow-sm">
                      <div className="flex items-center gap-2">
                         <span className="text-sm">🛡️</span>
                         <div className="flex flex-col">
                            <span className="text-[10px] font-black text-slate-700 uppercase leading-none">Firewall</span>
                            <span className={`text-[7px] font-bold uppercase mt-1 ${selectedDevice.firewallDisabled ? 'text-slate-400' : 'text-red-500'}`}>
                               {selectedDevice.firewallDisabled ? 'OFF' : 'ON'}
                            </span>
                         </div>
                      </div>
                      <button 
                        onClick={() => updateDeviceConfig(selectedId!, { firewallDisabled: !selectedDevice.firewallDisabled })} 
                        className={`w-10 h-5 rounded-full relative transition-all shadow-inner ${selectedDevice.firewallDisabled ? 'bg-slate-200' : 'bg-green-500'}`}
                      >
                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all shadow-md ${selectedDevice.firewallDisabled ? 'left-0.5' : 'left-5.5'}`}></div>
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => { setLinkingFrom(selectedId); setSelectedId(null); }} className="py-2.5 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg active:scale-95 transition-transform">Colok Kabel</button>
                    <button onClick={() => { setLinks(prev => prev.filter(l => l.from !== selectedId && l.to !== selectedId)); }} className="py-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl text-[9px] font-black uppercase active:scale-95">Lepas Kabel</button>
                  </div>

                  {selectedDevice.type === 'PC' && (
                    <section className="pt-4 border-t border-slate-100">
                      <div className="flex gap-1.5">
                        <input className="flex-1 px-3 py-2.5 bg-slate-900 text-green-400 rounded-lg text-[10px] font-mono outline-none border border-slate-700" placeholder="ping 100.100.100.1" value={pingIp} onChange={(e) => setPingIp(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handlePing()} />
                        <button onClick={handlePing} className="px-3 py-2.5 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase shadow-lg shadow-blue-100 active:scale-95 transition-all">TES</button>
                      </div>
                    </section>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-3">
                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-xl shadow-inner animate-pulse">👷</div>
                <p className="text-slate-400 text-[9px] font-black uppercase tracking-[2px]">Siap Beraksi</p>
                <p className="text-[10px] text-slate-400 leading-relaxed">Pilih alat dari daftar di atas untuk mulai memasang internet di rumah.</p>
              </div>
            )}
          </div>

          <div className="h-40 bg-slate-900 text-green-400 p-4 font-mono text-[10px] overflow-y-auto border-t border-slate-800 shadow-2xl">
            <p className="text-slate-500 text-[8px] font-black uppercase tracking-widest mb-2 border-b border-slate-800 pb-1">Log Jaringan (Console)</p>
            {pingLog.length === 0 ? <p className="opacity-20 italic">Sistem standby...</p> : pingLog.map((log, i) => <div key={i} className="mb-1 leading-tight border-l-2 border-green-900/50 pl-2">{log}</div>)}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default App;
